const SIGN_OUT_LABELS = new Set(["sign out", "log out"]);

const ACCOUNT_CSS_VARIABLES = {
  "--shopify-account-color-accent": "#010C45",
  "--shopify-account-color-accent-text": "#ffffff",
  "--shopify-account-color-accent-hover": "#010B45",
  "--shopify-account-color-background": "#ffffff",
  "--shopify-account-color-text": "#010C45",
  "--shopify-account-color-control-text": "#ffffff",
};

const ACCOUNT_HOST_STYLE = `:host {
  ${Object.entries(ACCOUNT_CSS_VARIABLES)
    .map(([name, value]) => `${name}: ${value}`)
    .join("; ")};
  border-radius: 50%;
  line-height: 0;
  overflow: hidden;
  max-height: 100%;
  padding: 0;
}`;

const ACCOUNT_TRIGGER_STYLE = `
  .dbi-account-trigger,
  [part="signed-out-avatar"],
  button[part="signed-out-avatar"] {
    background: transparent !important;
    border: 0 !important;
    padding: 0 !important;
    margin: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    min-width: 0;
    min-height: 0;
    border-radius: 50% !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    cursor: pointer;
    outline: none !important;
    box-shadow: none !important;
    line-height: 0;
    overflow: hidden;
    -webkit-appearance: none;
    appearance: none;
  }

  .dbi-account-trigger:focus,
  .dbi-account-trigger:focus-visible,
  .dbi-account-trigger:focus-within,
  [part="signed-out-avatar"]:focus,
  [part="signed-out-avatar"]:focus-visible,
  [part="signed-out-avatar"]:focus-within,
  button[part="signed-out-avatar"]:focus,
  button[part="signed-out-avatar"]:focus-visible,
  button[part="signed-out-avatar"]:focus-within {
    outline: none !important;
    box-shadow: none !important;
    border: 0 !important;
    background: transparent !important;
    border-radius: 50% !important;
  }
`;

function getAccountTrigger(account) {
  const root = account.shadowRoot;
  if (!root) return null;

  const dialog = root.querySelector('[role="dialog"]');
  const partTrigger = root.querySelector('[part="signed-out-avatar"]');

  if (partTrigger) return partTrigger;

  return (
    Array.from(root.querySelectorAll("button")).find(
      (button) => !dialog?.contains(button),
    ) || null
  );
}

function markAccountTrigger(account) {
  const trigger = getAccountTrigger(account);
  if (!trigger) return;

  trigger.classList.add("dbi-account-trigger");
  clearSquareTriggerFocus(trigger);
}

function clearSquareTriggerFocus(trigger) {
  trigger.style.setProperty("outline", "none", "important");
  trigger.style.setProperty("box-shadow", "none", "important");
  trigger.style.setProperty("border", "none", "important");
  trigger.style.setProperty("border-radius", "50%", "important");
  trigger.style.setProperty("background", "transparent", "important");
}

function bindAccountTriggerFocus(account) {
  if (!account.closest(".wt-header__panel__item--account")) return;

  const tryBind = () => {
    const trigger = getAccountTrigger(account);
    if (!trigger) return false;

    if (trigger.dataset.dbiFocusBound !== "true") {
      trigger.dataset.dbiFocusBound = "true";

      const suppressInnerFocus = () => clearSquareTriggerFocus(trigger);

      trigger.addEventListener("focus", suppressInnerFocus, true);
      trigger.addEventListener("focusin", suppressInnerFocus, true);
      trigger.addEventListener("blur", suppressInnerFocus, true);
    } else {
      clearSquareTriggerFocus(trigger);
    }

    return true;
  };

  tryBind();

  if (account.dataset.dbiTriggerFocusObserver === "true") return;
  account.dataset.dbiTriggerFocusObserver = "true";

  const observer = new MutationObserver(() => {
    tryBind();
  });

  if (account.shadowRoot) {
    observer.observe(account.shadowRoot, { childList: true, subtree: true });
  } else {
    observer.observe(account, { childList: true });
  }
}

function getAccountShadowRoots() {
  return Array.from(document.querySelectorAll("shopify-account"))
    .map((account) => account.shadowRoot)
    .filter(Boolean);
}

const ORDER_LIST_IMAGE_STYLE_ID = "dbi-order-list-image-contain";
const ORDER_LIST_IMAGE_CSS = `
  #order-list img._1fragemw8,
  #order-list img[class*="_1fragemw8"],
  #order-list img.dbi-order-list-image,
  #order-list img[src*="cdn.shopify.com"],
  img._1fragemw8[src*="cdn.shopify.com"],
  img[class*="_1fragemw8"][src*="cdn.shopify.com"] {
    object-fit: contain !important;
    object-position: center !important;
  }
`;

const ORDER_LIST_IFRAME_HELPER_ID = "dbi-order-list-image-helper";
const ORDER_LIST_IFRAME_HELPER = `
(function () {
  if (window.__dbiOrderListImageHelper) return;
  window.__dbiOrderListImageHelper = true;

  var STYLE_ID = ${JSON.stringify(ORDER_LIST_IMAGE_STYLE_ID)};
  var CSS = ${JSON.stringify(ORDER_LIST_IMAGE_CSS)};
  var IMAGE_SELECTOR =
    '#order-list img[src*="cdn.shopify.com"], img._1fragemw8[src*="cdn.shopify.com"], img[class*="_1fragemw8"][src*="cdn.shopify.com"]';

  function stripCover(img) {
    img.classList.remove("_1fragemw8");
    Array.prototype.forEach.call(img.classList, function (className) {
      if (className.indexOf("fragemw8") !== -1) {
        img.classList.remove(className);
      }
    });
  }

  function apply() {
    var style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      (document.head || document.documentElement).appendChild(style);
    }

    style.textContent = CSS;
    document.head.appendChild(style);

    document.querySelectorAll(IMAGE_SELECTOR).forEach(function (img) {
      stripCover(img);
      img.style.setProperty("object-fit", "contain", "important");
      img.style.setProperty("object-position", "center", "important");
    });
  }

  apply();
  new MutationObserver(apply).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style"],
  });
})();
`;

const watchedOrderListRoots = new WeakSet();
const adoptedOrderListSheets = new WeakMap();
const boundAccountIframes = new WeakSet();
let orderListPollTimer = null;

function getRootDocument(root) {
  if (root instanceof Document) return root;
  return root.ownerDocument || document;
}

function appendNodeLast(root, node) {
  if (!root?.appendChild || !node) return;

  if (root instanceof Document) {
    (root.head || root.documentElement).appendChild(node);
    return;
  }

  root.appendChild(node);
}

function injectIframeOrderListHelper(iframe) {
  try {
    const doc = iframe.contentDocument;
    if (!doc?.documentElement) {
      iframe.dataset.dbiAccountIframeCrossOrigin = "true";
      return false;
    }

    iframe.dataset.dbiAccountIframeCrossOrigin = "false";

    if (doc.getElementById(ORDER_LIST_IFRAME_HELPER_ID)) return true;

    const script = doc.createElement("script");
    script.id = ORDER_LIST_IFRAME_HELPER_ID;
    script.textContent = ORDER_LIST_IFRAME_HELPER;
    (doc.head || doc.documentElement).appendChild(script);
    return true;
  } catch {
    iframe.dataset.dbiAccountIframeCrossOrigin = "true";
    return false;
  }
}

function bindAccountIframe(iframe) {
  if (boundAccountIframes.has(iframe)) return;
  boundAccountIframes.add(iframe);

  const tryInject = () => {
    injectIframeOrderListHelper(iframe);
    normalizeOrderListImages();
  };

  iframe.addEventListener("load", tryInject);
  tryInject();
}

function injectAccountIframeHelpers() {
  document.querySelectorAll("shopify-account").forEach((account) => {
    account.shadowRoot?.querySelectorAll("iframe").forEach(bindAccountIframe);
  });
}

function startOrderListPolling() {
  clearInterval(orderListPollTimer);

  let attempts = 0;
  orderListPollTimer = setInterval(() => {
    injectAccountIframeHelpers();
    normalizeOrderListImages();
    attempts += 1;

    if (attempts >= 40) {
      clearInterval(orderListPollTimer);
    }
  }, 500);
}

function collectOrderListRoots() {
  const roots = [];
  const queue = [document];
  const seen = new Set();

  while (queue.length) {
    const current = queue.shift();
    if (!current || seen.has(current)) continue;
    seen.add(current);

    if (current.querySelector?.("#order-list")) {
      roots.push(current);
    }

    const elements = current.querySelectorAll?.("*") || [];
    elements.forEach((element) => {
      if (element.shadowRoot) {
        queue.push(element.shadowRoot);
      }

      if (element.tagName === "IFRAME") {
        try {
          const iframeDoc = element.contentDocument;
          if (iframeDoc) queue.push(iframeDoc);
        } catch {
          // Cross-origin account iframe — cannot inject styles from the theme.
        }
      }
    });
  }

  return roots;
}

function stripObjectFitCoverClass(img) {
  img.classList.remove("_1fragemw8");

  Array.from(img.classList).forEach((className) => {
    if (className.includes("fragemw8")) {
      img.classList.remove(className);
    }
  });
}

function applyOrderListImageContain(img) {
  stripObjectFitCoverClass(img);
  img.classList.add("dbi-order-list-image");
  img.style.setProperty("object-fit", "contain", "important");
  img.style.setProperty("object-position", "center", "important");
}

function injectAdoptedOrderListStyles(root) {
  if (!root?.adoptedStyleSheets) return;

  let sheet = adoptedOrderListSheets.get(root);

  if (!sheet) {
    sheet = new CSSStyleSheet();
    adoptedOrderListSheets.set(root, sheet);
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  }

  sheet.replaceSync(ORDER_LIST_IMAGE_CSS);
}

function ensureOrderListStyleLast(root) {
  const style = root.getElementById?.(ORDER_LIST_IMAGE_STYLE_ID);
  if (!style) return;
  appendNodeLast(root, style);
}

function watchOrderListRoot(root) {
  if (!root?.querySelector || watchedOrderListRoots.has(root)) return;
  watchedOrderListRoots.add(root);

  const observer = new MutationObserver(() => {
    injectOrderListImageStyles(root);
  });

  observer.observe(root, { childList: true, subtree: true });
}

function watchOrderListImage(img) {
  if (img.dataset.dbiOrderListWatch === "true") return;
  img.dataset.dbiOrderListWatch = "true";

  const reapply = () => {
    applyOrderListImageContain(img);

    if (getComputedStyle(img).objectFit === "cover") {
      stripObjectFitCoverClass(img);
      img.style.setProperty("object-fit", "contain", "important");
    }
  };

  reapply();

  const observer = new MutationObserver(reapply);
  observer.observe(img, {
    attributes: true,
    attributeFilter: ["class", "style"],
  });
}

function injectOrderListImageStyles(root) {
  if (!root?.querySelector) return;

  watchOrderListRoot(root);
  injectAdoptedOrderListStyles(root);

  const ownerDoc = getRootDocument(root);
  let style = root.getElementById?.(ORDER_LIST_IMAGE_STYLE_ID);
  if (!style) {
    style = ownerDoc.createElement("style");
    style.id = ORDER_LIST_IMAGE_STYLE_ID;
    appendNodeLast(root, style);
  }

  style.textContent = ORDER_LIST_IMAGE_CSS;
  ensureOrderListStyleLast(root);

  root
    .querySelectorAll('#order-list img[src*="cdn.shopify.com"]')
    .forEach(watchOrderListImage);
}

function normalizeOrderListImages() {
  injectAccountIframeHelpers();
  collectOrderListRoots().forEach(injectOrderListImageStyles);
}

function watchAccountOrderList(account) {
  const root = account.shadowRoot;
  if (!root) return;

  if (account.dataset.dbiOrderListWatch !== "true") {
    account.dataset.dbiOrderListWatch = "true";

    const observer = new MutationObserver(() => {
      normalizeOrderListImages();
    });

    observer.observe(root, { childList: true, subtree: true });

    root.querySelectorAll("iframe").forEach((iframe) => {
      iframe.addEventListener("load", normalizeOrderListImages);
    });
  }

  normalizeOrderListImages();
}

function injectAccountStyles(account) {
  if (!account.shadowRoot) return false;

  const styleId = "dbi-shopify-account-vars";
  let style = account.shadowRoot.getElementById(styleId);

  if (!style) {
    style = document.createElement("style");
    style.id = styleId;
    account.shadowRoot.prepend(style);
  }

  style.textContent = `${ACCOUNT_HOST_STYLE}\n${ACCOUNT_TRIGGER_STYLE}`;
  markAccountTrigger(account);
  bindAccountTriggerFocus(account);
  return true;
}

function watchAccountShadowRoot(account) {
  if (injectAccountStyles(account)) return;

  if (account.dataset.dbiAccountStyleObserver === "true") return;
  account.dataset.dbiAccountStyleObserver = "true";

  const shadowObserver = new MutationObserver(() => {
    if (injectAccountStyles(account)) {
      markAccountTrigger(account);
      shadowObserver.disconnect();
    }
  });

  shadowObserver.observe(account, { childList: true });
}

function normalizeButtonLabel(text) {
  return text?.replace(/\s+/g, " ").trim().toLowerCase() || "";
}

function markSignOutButtons() {
  getAccountShadowRoots().forEach((root) => {
    root.querySelectorAll("button").forEach((button) => {
      if (!SIGN_OUT_LABELS.has(normalizeButtonLabel(button.textContent))) return;

      button.classList.add("dbi-account-sign-out");
      button.style.setProperty("color", "#ffffff", "important");
      button.style.setProperty("background-color", "#010C45", "important");

      button.querySelectorAll("span").forEach((span) => {
        span.classList.add("dbi-account-sign-out__text");
        span.style.setProperty("color", "#ffffff", "important");
      });
    });
  });
}

function applyInlineVariables(element) {
  Object.entries(ACCOUNT_CSS_VARIABLES).forEach(([name, value]) => {
    element.style.setProperty(name, value);
  });
}

function setupAccount(account) {
  applyInlineVariables(account);
  watchAccountShadowRoot(account);
  watchAccountOrderList(account);
  bindAccountTriggerFocus(account);

  if (account.dataset.dbiAccountListener === "true") return;
  account.dataset.dbiAccountListener = "true";
  account.addEventListener("open", () => {
    injectAccountStyles(account);
    scheduleAccountDialogStyling();
    startOrderListPolling();
  });
}

function scheduleAccountDialogStyling() {
  document.querySelectorAll("shopify-account").forEach((account) => {
    markAccountTrigger(account);
    bindAccountTriggerFocus(account);
    watchAccountOrderList(account);
  });
  markSignOutButtons();
  normalizeOrderListImages();
  startOrderListPolling();
  requestAnimationFrame(() => {
    markSignOutButtons();
    normalizeOrderListImages();
  });
  [100, 400, 1000, 2000, 4000].forEach((delay) => {
    setTimeout(normalizeOrderListImages, delay);
  });
}

function removeLegacyOrderImageStyles() {
  document.getElementById("dbi-order-product-image-styles-doc")?.remove();

  getAccountShadowRoots().forEach((root) => {
    root.getElementById("dbi-order-product-image-styles")?.remove();
  });
}

function mutationAddsAccountElement(mutations) {
  return mutations.some((mutation) =>
    Array.from(mutation.addedNodes).some((node) => {
      if (!(node instanceof Element)) return false;
      return (
        node.tagName === "SHOPIFY-ACCOUNT" ||
        Boolean(node.querySelector?.("shopify-account"))
      );
    }),
  );
}

function init() {
  removeLegacyOrderImageStyles();
  normalizeOrderListImages();
  document.querySelectorAll("shopify-account").forEach(setupAccount);

  const observer = new MutationObserver((mutations) => {
    if (!mutationAddsAccountElement(mutations)) return;

    document.querySelectorAll("shopify-account").forEach(setupAccount);
    clearTimeout(init.accountDialogObserverTimer);
    init.accountDialogObserverTimer = setTimeout(scheduleAccountDialogStyling, 100);
  });

  observer.observe(document.body, { childList: true, subtree: true });
  scheduleAccountDialogStyling();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
